/**
 * PhoneLogin — the mobile capture page at `/login/phone?token=<tok>`.
 *
 * This is the page a QR rendered on the desktop login step points at. The
 * phone scans, lands here, takes a selfie / any photo, and POSTs it to
 * `/api/auth/photo-login?login_token=<tok>`. The token carries the handoff —
 * the BE stamps the login-token row with the new `session_id`, and the
 * desktop's poll on `/api/auth/login-session/:tok` flips to `ready` on the
 * next tick. The phone never reads back the session.
 *
 * Mirrors PhoneUpload.tsx: no scene chrome, big tap targets, native camera
 * via `<input capture="environment">`.
 */
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Camera, CheckCircle2, Loader2, XCircle } from 'lucide-react'

import { usePhonePhotoLogin } from '@/hooks/queries'
import { ApiError } from '@/lib/api'

export default function PhoneLogin() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const login = usePhonePhotoLogin(token)

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setPreviewUrl(URL.createObjectURL(file))
    login.mutate(file)
  }

  if (!token) {
    return (
      <PhoneFrame>
        <StatusBlock
          icon={<XCircle className="h-12 w-12 text-pop-fire" strokeWidth={2.5} />}
          title="Chybí kód"
          message="Tahle stránka se otevírá naskenováním QR kódu na obrazovce počítače."
        />
      </PhoneFrame>
    )
  }

  if (login.isSuccess) {
    return (
      <PhoneFrame>
        <StatusBlock
          icon={<CheckCircle2 className="h-12 w-12 text-pop-covered" strokeWidth={2.5} />}
          title="Přihlášeno!"
          message="Vrať se k obrazovce počítače — pozdraví tě tam."
        />
      </PhoneFrame>
    )
  }

  const tokenDead =
    login.isError && login.error instanceof ApiError && login.error.status === 410

  return (
    <PhoneFrame>
      <h1 className="text-2xl font-semibold uppercase tracking-wide text-pop-silhouette text-center">
        Vyfoť se
      </h1>
      <p className="text-sm text-pop-shadow text-center max-w-xs">
        Stačí jakákoliv fotka — pozdravíme tě jménem.
      </p>

      {previewUrl && (
        <img
          src={previewUrl}
          alt="Náhled"
          className="w-40 h-40 object-cover border-[3px] border-pop-silhouette"
        />
      )}

      {login.isPending ? (
        <div className="flex items-center gap-2 text-pop-silhouette font-mono uppercase text-sm">
          <Loader2 className="h-5 w-5 animate-spin text-pop-fire" strokeWidth={3} />
          <span>Přihlašuji…</span>
        </div>
      ) : (
        <>
          <button
            onClick={() => inputRef.current?.click()}
            className="pixel-btn flex items-center gap-2 text-lg px-6 py-4"
          >
            <Camera className="h-6 w-6" strokeWidth={2.5} />
            {login.isError ? 'Zkusit znovu' : 'Vybrat fotku nebo soubor'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPick}
          />
        </>
      )}

      {login.isError && (
        <p className="text-sm text-pop-fire text-center max-w-xs">
          {tokenDead
            ? 'Kód vypršel nebo už byl použit. Obnov QR kód na počítači a naskenuj znovu.'
            : 'Přihlášení se nepovedlo. Zkus to prosím znovu.'}
        </p>
      )}
    </PhoneFrame>
  )
}

function PhoneFrame({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 py-10 bg-pop-cream text-pop-silhouette">
      {children}
    </main>
  )
}

function StatusBlock({
  icon,
  title,
  message,
}: {
  icon: ReactNode
  title: string
  message: string
}) {
  return (
    <div className="flex flex-col items-center gap-4 text-center max-w-xs">
      <div className="bg-pop-cream border-[3px] border-pop-silhouette p-4">{icon}</div>
      <h1 className="text-2xl font-semibold uppercase tracking-wide">{title}</h1>
      <p className="text-sm text-pop-shadow">{message}</p>
    </div>
  )
}
